import '../styles/PageGlobal.css';
import './ContactNewPage.css';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReferenceButton from '../components/ReferenceButton';
import { ContactService } from '../services/ContactService.js';
import { ClientService } from '../services/ClientService.js';
import { FournisseurService } from '../services/FournisseurService.js';

export default function ContactNewPage() {
  const navigate = useNavigate();
  const contactService = useMemo(() => new ContactService(), []);
  const clientService = useMemo(() => new ClientService(), []);
  const fournisseurService = useMemo(() => new FournisseurService(), []);
  const wrapperRef = useRef(null); // Pour fermer le dropdown si on clique ailleurs

  const [formData, setFormData] = useState({
    nom: '', prenom: '', email1: '', email2: '',
    telephone1: '', telephone2: '', commentaire: ''
  });

  const [typeContact, setTypeContact] = useState("CLIENT");
  const [selectedId, setSelectedId] = useState("");
  const [searchQuery, setSearchQuery] = useState(""); 
  
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false); // Contrôle l'affichage de la liste
  
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);

  // 🟢 EFFET DE RECHERCHE AVEC DEBOUNCE
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchQuery.length >= 2 && !selectedId) { // Ne cherche que si pas déjà sélectionné
        setSearching(true);
        try {
          if (typeContact === "CLIENT") {
            const results = await clientService.searchClients(searchQuery);
            setSearchResults(results || []);
          } else if (typeContact === "FOURNISSEUR"){
            const results = await fournisseurService.searchFournisseurs(searchQuery);
            setSearchResults(results || []);
          }
          setShowDropdown(true);
        } catch (err) {
          console.error("Erreur recherche:", err);
          setSearchResults([]); // Pas d'erreur bloquante, juste vide
        } finally {
          setSearching(false);
        }
      } else if (searchQuery.length < 2) {
        setSearchResults([]);
        setShowDropdown(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchQuery, typeContact, selectedId, clientService, fournisseurService]);

  // Fermer le dropdown si on clique en dehors
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [wrapperRef]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTypeChange = (newType) => {
    setTypeContact(newType);
    setSelectedId(""); 
    setSearchQuery(""); 
    setSearchResults([]);
    setShowDropdown(false);
    setError(null);
  };

  // 🟢 SÉLECTION D'UN ÉLÉMENT DANS LA LISTE (Style Google)
  const handleSelectEntity = (entity) => {
    setSelectedId(entity.id);
    // On affiche le nom choisi dans la barre de recherche
    const displayName = entity.societe1 || entity.societe2 || entity.contact || `Entité #${entity.id}`;
    setSearchQuery(displayName);
    setShowDropdown(false); // On ferme la liste
  };

  const handleClearSelection = () => {
    setSelectedId("");
    setSearchQuery("");
    setShowDropdown(false);
  };

  const handleSave = async () => {
    if (!formData.nom) {
      setError('Le nom du contact est requis');
      return;
    }
    if (!selectedId) {
      setError(`Veuillez sélectionner un ${typeContact === "CLIENT" ? "client" : "fournisseur"} dans la liste`);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const payload = {
        ...formData,
        ...(typeContact === "CLIENT"
          ? { client: { id: Number(selectedId) } }
          : { fournisseur: { id: Number(selectedId) } }
        )
      };

      await contactService.createContact(payload);
      alert('Contact créé avec succès !');
      navigate('/contact_list');
    } catch (err) {
      setError(err.message || "Erreur lors de la création");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="containerMain contactnew-container">
      <div className="contactnew-header">
        <h1 className="contactnew-title">Créer un Contact</h1>
        <div className="contactnew-header-actions">
          <ReferenceButton onClick={() => navigate('/contact_list')} className="contactnew-btn contactnew-btn-cancel">Annuler</ReferenceButton>
          <ReferenceButton onClick={handleSave} className="contactnew-btn contactnew-btn-save" disabled={loading}>
            {loading ? 'Création...' : ' Enregistrer le contact'}
          </ReferenceButton>
        </div>
      </div>

      {/* Affichage des erreurs de formulaire (pas de recherche) */}
      {error && (
        <div className="contactnew-status contactnew-status-error" style={{ marginBottom: '15px' }}>
          {error}
          <button onClick={() => setError(null)} style={{ marginLeft: '10px', background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      <div className="contactnew-form-grid" style={{ marginBottom: '20px' }}>
        <fieldset className="contactnew-fieldset">
          <legend>Lier à une entité *</legend>

          <div className="contactnew-form-row" style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Type d'entité :</label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="button" onClick={() => handleTypeChange("CLIENT")}
                style={{ flex: 1, padding: '10px', borderRadius: '4px', cursor: 'pointer', backgroundColor: typeContact === "CLIENT" ? '#007bff' : '#e9ecef', color: typeContact === "CLIENT" ? 'white' : '#333', border: 'none', fontWeight: 'bold' }}>
                Client
              </button>
              <button type="button" onClick={() => handleTypeChange("FOURNISSEUR")}
                style={{ flex: 1, padding: '10px', borderRadius: '4px', cursor: 'pointer', backgroundColor: typeContact === "FOURNISSEUR" ? '#007bff' : '#e9ecef', color: typeContact === "FOURNISSEUR" ? 'white' : '#333', border: 'none', fontWeight: 'bold' }}>
                Fournisseur
              </button>
            </div>
          </div>

          <div className="contactnew-form-row" ref={wrapperRef} style={{ position: 'relative' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              {typeContact === "CLIENT" ? "Rechercher un Client *" : "Rechercher un Fournisseur *"}
            </label>
            
            {/* 🟢 BARRE DE RECHERCHE STYLE GOOGLE */}
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder={typeContact === "CLIENT" ? "Tapez une société, un contact ou un email..." : "Tapez une société ou un contact..."}
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setSelectedId(""); setShowDropdown(true); }}
                onFocus={() => { if(searchQuery.length >= 2 && searchResults.length > 0) setShowDropdown(true); }}
                style={{ 
                  width: '100%', padding: '10px', paddingRight: selectedId ? '30px' : '10px',
                  borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px', boxSizing: 'border-box'
                }}
              />
              {selectedId && (
                <button 
                  type="button" 
                  onClick={handleClearSelection}
                  style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#999' }}
                  title="Effacer la sélection"
                >
                  ✕
                </button>
              )}
            </div>

            {/* 🟢 DROPDOWN PERSONNALISÉ (AUTOCOMPLÉTION) */}
            {showDropdown && searchQuery.length >= 2 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0,
                backgroundColor: 'white', border: '1px solid #ccc', borderTop: 'none',
                borderRadius: '0 0 4px 4px', maxHeight: '250px', overflowY: 'auto',
                zIndex: 1000, boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}>
                {searching ? (
                  <div style={{ padding: '10px', color: '#666', textAlign: 'center' }}>Recherche en cours...</div>
                ) : searchResults.length === 0 ? (
                  <div style={{ padding: '10px', color: '#999', textAlign: 'center' }}>Aucun résultat trouvé</div>
                ) : (
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                   {searchResults.map(entity => (
                    <li 
                      key={entity.id} 
                      onClick={() => handleSelectEntity(entity)}
                      style={{ 
                        padding: '10px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f9ff'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      {/* 🟢 CONDITION : Si c'est un CLIENT */}
                      {typeContact === "CLIENT" ? (
                        <>
                          <strong>{entity.societe1 || entity.contact || `Client #${entity.id}`}</strong>
                          {entity.email && <span style={{ color: '#666', fontSize: '12px', marginLeft: '8px' }}>✉️ {entity.email}</span>}
                        </>
                      ) : (
                        /* 🟢 SINON (ELSE) : C'est un FOURNISSEUR */
                        <>
                          {/* 1. Nom principal : societe1, sinon societe2, sinon contact, sinon ID par défaut */}
                          <strong>{entity.societe1 || entity.societe2 || entity.contact || `Fournisseur #${entity.id}`}</strong>
                          
                          {/* 2. Si les deux sociétés existent, on affiche la 2ème entre parenthèses */}
                          {entity.societe2 && entity.societe1 && (
                            <span style={{ color: '#666', fontSize: '12px', marginLeft: '5px' }}>
                              ({entity.societe2})
                            </span>
                          )}
                          
                          {/* 3. Affichage du téléphone s'il existe */}
                          {entity.telephone && (
                            <span style={{ color: '#666', fontSize: '12px', marginLeft: '8px' }}>
                              📞 {entity.telephone}
                            </span>
                          )}
                        </>
                      )}
                    </li>
                  ))}
                  </ul>
                )}
              </div>
            )}
            
            <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
              {selectedId ? "Entité sélectionnée." : "La recherche démarre à partir de 2 caractères."}
            </small>
          </div>
        </fieldset>
      </div>

      <div className="contactnew-form-grid">
        <fieldset className="contactnew-fieldset">
          <legend>Informations Personnelles</legend>
          <div className="contactnew-form-row">
            <label>Nom *<input type="text" value={formData.nom} onChange={(e) => handleInputChange('nom', e.target.value)} required /></label>
          </div>
          <div className="contactnew-form-row">
            <label>Prénom<input type="text" value={formData.prenom} onChange={(e) => handleInputChange('prenom', e.target.value)} /></label>
          </div>
          <div className="contactnew-form-row">
            <label>Email 1<input type="email" value={formData.email1} onChange={(e) => handleInputChange('email1', e.target.value)} /></label>
          </div>
          <div className="contactnew-form-row">
            <label>Email 2<input type="email" value={formData.email2} onChange={(e) => handleInputChange('email2', e.target.value)} /></label>
          </div>
          <div className="contactnew-form-row">
            <label>Téléphone 1<input type="tel" value={formData.telephone1} onChange={(e) => handleInputChange('telephone1', e.target.value)} /></label>
          </div>
          <div className="contactnew-form-row">
            <label>Téléphone 2<input type="tel" value={formData.telephone2} onChange={(e) => handleInputChange('telephone2', e.target.value)} /></label>
          </div>
        </fieldset>

        <fieldset className="contactnew-fieldset">
          <legend>Autres Informations</legend>
          <div className="contactnew-form-row">
            <label>Commentaire<textarea value={formData.commentaire} onChange={(e) => handleInputChange('commentaire', e.target.value)} rows="4" /></label>
          </div>
        </fieldset>
      </div>
    </div>
  );
}